// src/services/planningExecutionApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

import { Phase, Task, Action, TaskAssignment } from '../types';

export interface PlanningExecutionApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR' | 'LOCKED' | 'PREREQUISITES_NOT_MET';
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// ============================================================================
// Phase Endpoints
// ============================================================================

export type PhaseResponse = Phase;

export interface CreatePhaseRequest {
  name: string;
  description?: string | null;
  displayOrder: number;
  plannedStartDate?: string | null; // ISO 8601 date
  plannedEndDate?: string | null; // ISO 8601 date
  plannedDurationHours?: number;
}

export interface UpdatePhaseRequest {
  name?: string;
  description?: string | null;
  displayOrder?: number;
  plannedStartDate?: string | null; // ISO 8601 date
  plannedEndDate?: string | null; // ISO 8601 date
  plannedDurationHours?: number;
}

// Get all phases for a project
export async function getPhases(projectId: number): Promise<PhaseResponse[]> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/phases`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get phases: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  const data = await response.json();
  const phases = Array.isArray(data) ? data : data.phases || [];
  
  // Log raw API response for debugging date issues
  if (phases.length > 0) {
    console.log(`[API] getPhases(${projectId}) - Raw API response dates:`, phases.map((p: { phaseId?: number; name?: string; plannedStartDate?: unknown; plannedEndDate?: unknown }) => ({
      phaseId: p.phaseId,
      name: p.name,
      plannedStartDate: p.plannedStartDate,
      plannedEndDate: p.plannedEndDate,
      plannedStartDateType: typeof p.plannedStartDate,
      plannedEndDateType: typeof p.plannedEndDate,
    })));
  }
  
  return phases;
}

// Create a new phase
export async function createPhase(projectId: number, data: CreatePhaseRequest): Promise<PhaseResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/phases`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to create phase: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Update a phase
export async function updatePhase(phaseId: number, data: UpdatePhaseRequest): Promise<PhaseResponse> {
  const response = await fetch(`${API_BASE_URL}/api/phases/${phaseId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Phase not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update phase: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Delete a phase
export async function deletePhase(phaseId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/phases/${phaseId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Phase not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to delete phase: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }
}

// ============================================================================
// Task Endpoints
// ============================================================================

export type TaskResponse = Task;

export interface CreateTaskRequest {
  name: string;
  description?: string | null;
  displayOrder: number;
  plannedStartDate?: string | null; // ISO 8601 date
  plannedEndDate?: string | null; // ISO 8601 date
  plannedDurationHours?: number;
  prerequisiteTaskIds?: number[]; // Backend expects prerequisiteTaskIds (not prerequisites)
  parallelExecution?: boolean;
}

export interface UpdateTaskRequest {
  name?: string;
  description?: string | null;
  displayOrder?: number;
  plannedStartDate?: string | null; // ISO 8601 date
  plannedEndDate?: string | null; // ISO 8601 date
  plannedDurationHours?: number;
  prerequisiteTaskIds?: number[]; // Backend expects prerequisiteTaskIds (not prerequisites)
  parallelExecution?: boolean;
}

// Get all tasks for a phase
export async function getTasks(phaseId: number): Promise<TaskResponse[]> {
  const response = await fetch(`${API_BASE_URL}/api/phases/${phaseId}/tasks`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Phase not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get tasks: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  const data = await response.json();
  return Array.isArray(data) ? data : data.tasks || [];
}

// Create a new task
export async function createTask(phaseId: number, data: CreateTaskRequest): Promise<TaskResponse> {
  const response = await fetch(`${API_BASE_URL}/api/phases/${phaseId}/tasks`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to create task: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Update a task
export async function updateTask(taskId: number, data: UpdateTaskRequest): Promise<TaskResponse> {
  const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Task not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update task: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Delete a task
export async function deleteTask(taskId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Task not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to delete task: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }
}

// ============================================================================
// Action Endpoints
// ============================================================================

export type ActionResponse = Action;

export interface CreateActionRequest {
  actionType: 'CREATE' | 'REVIEW' | 'REVISE' | 'APPROVE';
  duration?: number; // in hours (kept for backward compatibility)
  durationHours: number; // Backend requires this field name (required)
  notes?: string | null;
  displayOrder: number;
  plannedStartDate?: string | null; // ISO 8601 date
  plannedEndDate?: string | null; // ISO 8601 date
  plannedDurationHours?: number;
}

export interface UpdateActionRequest {
  actionType?: 'CREATE' | 'REVIEW' | 'REVISE' | 'APPROVE';
  duration?: number; // in hours
  notes?: string | null;
  displayOrder?: number;
  plannedStartDate?: string | null; // ISO 8601 date
  plannedEndDate?: string | null; // ISO 8601 date
  plannedDurationHours?: number;
  sourceActionId?: number | null; // For REVISE, APPROVE, and REVIEW actions
  sourceSpItemId?: number | null; // For REVISE, APPROVE, and REVIEW actions
  requiredDocumentCount?: number | null; // For CREATE actions
}

// Get all actions for a task
export async function getActions(taskId: number): Promise<ActionResponse[]> {
  const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/actions`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Task not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get actions: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  const data = await response.json();
  return Array.isArray(data) ? data : data.actions || [];
}

// Create a new action
export async function createAction(taskId: number, data: CreateActionRequest): Promise<ActionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/actions`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to create action: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Update an action
export async function updateAction(actionId: number, data: UpdateActionRequest): Promise<ActionResponse> {
  console.log('updateAction called:', { actionId, data });
  const response = await fetch(`${API_BASE_URL}/api/actions/${actionId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Action not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    // Log the full error response for debugging
    console.error('Backend validation error (400):', {
      actionId,
      requestData: data,
      errorResponse: error,
      errorMessage: error.message,
      errorError: error.error,
      errorDetail: error.detail,
      errorErrors: error.errors,
      fullError: JSON.stringify(error, null, 2),
      status: response.status,
      statusText: response.statusText
    });
    throw {
      error: true,
      message: error.message || error.error || error.detail || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
      response: error, // Include full response for debugging
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update action: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Delete an action
export async function deleteAction(actionId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/actions/${actionId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Action not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to delete action: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }
}

// ============================================================================
// Task Assignment Endpoints
// ============================================================================

// Get all user assignments for a task
export async function getTaskAssignments(taskId: number): Promise<TaskAssignment[]> {
  const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/assignments`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Task not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get task assignments: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  const data = await response.json();
  return Array.isArray(data) ? data : data.assignments || [];
}

// Assign a user to a task
export async function assignUserToTask(taskId: number, userId: number): Promise<TaskAssignment> {
  const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/assignments`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({ userId }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to assign user to task: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Remove a user assignment from a task
export async function removeTaskAssignment(taskId: number, userId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/assignments/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Assignment not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to remove task assignment: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }
}

// ============================================================================
// Action Assignment Endpoints
// ============================================================================

export interface ActionAssignment {
  actionId: number;
  userId: number;
  user: {
    userId: number;
    email: string;
    displayName: string | null;
  };
}

// Get all user assignments for an action
export async function getActionAssignments(actionId: number): Promise<ActionAssignment[]> {
  const response = await fetch(`${API_BASE_URL}/api/actions/${actionId}/assignments`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Action not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get action assignments: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  const data = await response.json();
  return Array.isArray(data) ? data : data.assignments || [];
}

// Assign a user to an action (backward compatible - single user)
export async function assignUserToAction(actionId: number, userId: number): Promise<ActionAssignment> {
  const response = await fetch(`${API_BASE_URL}/api/actions/${actionId}/assignments`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({ userId }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to assign user to action: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Assign multiple users to an action (new multi-select support)
export async function assignUsersToAction(actionId: number, userIds: number[]): Promise<ActionAssignment[]> {
  const response = await fetch(`${API_BASE_URL}/api/actions/${actionId}/assignments`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({ userIds }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to assign users to action: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  const data = await response.json();
  // Backend returns array when userIds is provided, single object when userId is provided
  return Array.isArray(data) ? data : [data];
}

// Remove a user assignment from an action
export async function removeActionAssignment(actionId: number, userId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/actions/${actionId}/assignments/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Assignment not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to remove action assignment: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }
}

// ============================================================================
// Execution Plan Endpoints
// ============================================================================

export interface ExecutionPlanResponse {
  projectId: number;
  projectName: string;
  projectStartDate: string; // ISO 8601 date - original project start date
  currentProjectDate?: string; // ISO 8601 date - date when project was locked (for date simulation)
  phases: Phase[]; // Phases with nested tasks and actions
}

// No body needed - creates from planning data
export type CreateExecutionPlanRequest = Record<string, never>;

export interface UpdateExecutionPhaseRequest {
  actualStartDate?: string | null; // ISO 8601 date
  actualEndDate?: string | null; // ISO 8601 date
  actualDurationHours?: number;
  status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE' | 'LOCKED';
}

export interface UpdateExecutionTaskRequest {
  actualStartDate?: string | null; // ISO 8601 date
  actualEndDate?: string | null; // ISO 8601 date
  actualDurationHours?: number;
  status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE' | 'LOCKED';
}

export interface UpdateExecutionActionRequest {
  actualStartDate?: string | null; // ISO 8601 date
  actualEndDate?: string | null; // ISO 8601 date
  actualDurationHours?: number;
  status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE' | 'LOCKED';
}

// Create execution plan from planning data
export async function createExecutionPlan(projectId: number): Promise<ExecutionPlanResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/execution`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to create execution plan: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Get execution plan with nested phases/tasks/actions
export async function getExecutionPlan(projectId: number): Promise<ExecutionPlanResponse> {
  const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/execution`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Execution plan not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get execution plan: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Update execution data for a phase
export async function updateExecutionPhase(
  phaseId: number,
  data: UpdateExecutionPhaseRequest
): Promise<PhaseResponse> {
  const response = await fetch(`${API_BASE_URL}/api/execution/phases/${phaseId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Phase is locked and cannot be modified', code: 'LOCKED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Phase not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: error.code === 'PREREQUISITES_NOT_MET' ? 'PREREQUISITES_NOT_MET' : 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update execution phase: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Update execution data for a task
export async function updateExecutionTask(
  taskId: number,
  data: UpdateExecutionTaskRequest
): Promise<TaskResponse> {
  const response = await fetch(`${API_BASE_URL}/api/execution/tasks/${taskId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Task is locked and cannot be modified', code: 'LOCKED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Task not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: error.code === 'PREREQUISITES_NOT_MET' ? 'PREREQUISITES_NOT_MET' : 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update execution task: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// Update execution data for an action
export async function updateExecutionAction(
  actionId: number,
  data: UpdateExecutionActionRequest
): Promise<ActionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/execution/actions/${actionId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Action is locked and cannot be modified', code: 'LOCKED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Action not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: error.code === 'PREREQUISITES_NOT_MET' ? 'PREREQUISITES_NOT_MET' : 'VALIDATION_ERROR',
      errors: error.errors,
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update execution action: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// ============================================================================
// Organization Stats Endpoints
// ============================================================================

export interface OrganizationStat {
  type: string;
  kpiScore: number;
  userCount: number;
  taskCount: number;
  completedTaskCount: number;
}

export interface OrganizationStatsResponse {
  projectId: number;
  organizations: OrganizationStat[];
}

/**
 * Get organization statistics for a project
 * Returns +/- KPI scores for Fraport, Tenant, and Client organization groups
 */
export async function getOrganizationStats(
  projectId: number
): Promise<OrganizationStatsResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/projects/${projectId}/organization-stats`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...createAuthHeaders(),
      },
    }
  );

  if (response.status === 401) {
    throw {
      error: true,
      message: 'Authentication required',
      code: 'AUTH_REQUIRED',
    } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw {
      error: true,
      message: 'Project not found',
      code: 'NOT_FOUND',
    } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: errorData.message || `Failed to get organization stats: ${response.statusText}`,
      code: errorData.code || 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

// ============================================================================
// Action Completion Status Endpoints
// ============================================================================

export interface CompletionStatus {
  actionId: number;
  isCompleted: boolean;
  completedBy?: {
    userId: number;
    displayName: string;
    email: string;
  };
  completedAt?: string; // ISO 8601
  details?: {
    spItemId?: number;
    fileName?: string;
    uploadedAt?: string;
    [key: string]: any;
  };
}

/**
 * Get completion status for a CREATE or REVISE action
 * Returns completion details if the action has been completed, otherwise returns isCompleted: false
 */
export async function getActionCompletionStatus(
  actionId: number
): Promise<CompletionStatus> {
  const response = await fetch(`${API_BASE_URL}/api/actions/${actionId}/completion-status`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Access denied', code: 'AUTH_REQUIRED' } as PlanningExecutionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Action not found', code: 'NOT_FOUND' } as PlanningExecutionApiError;
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw {
      error: true,
      message: error.message || 'Failed to get completion status',
      code: 'NETWORK_ERROR',
    } as PlanningExecutionApiError;
  }

  return response.json();
}

